import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { API_URL } from '@core/config/api-url.token';
import { API_ENDPOINTS } from '@shared/constants/api-endpoints.const';
import { Observable } from 'rxjs';
import { AppointmentDetailDto } from '../../dtos/appointment-detail.dto';
import { ClientRefDto, PagedClientRefsDto } from '../../dtos/client-ref.dto';
import { DashboardSummaryDto } from '../../dtos/dashboard-summary.dto';
import { PagedProfessionalsDto } from '../../dtos/professional.dto';
import { PagedExternalResourcesDto } from '../../dtos/reason-type.dto';
import { PagedTasksDto, TaskDto } from '../../dtos/task.dto';

@Injectable()
export class DashboardRemoteDatasource {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(API_URL);

  public getSummary(departmentId?: number): Observable<DashboardSummaryDto> {
    return this.http.get<DashboardSummaryDto>(`${this.apiUrl}${API_ENDPOINTS.dashboard.summary}`, {
      params: departmentId === undefined ? {} : { departmentId },
    });
  }

  // Cross-controller on purpose -- reason-types are an appointments-scoped lookup
  // (AppointmentsController), not a dashboard one, but the appointments panel's subtitle needs
  // the reason-type display name to make sense of DashboardAppointmentDto.ReasonTypeId. Mirrors
  // AppointmentsRemoteDatasource.listReasonTypes(); dashboard owns its own copy of the DTO/model
  // per the architecture rule forbidding cross-feature imports between dashboard and appointments.
  public listReasonTypes(): Observable<PagedExternalResourcesDto> {
    return this.http.get<PagedExternalResourcesDto>(`${this.apiUrl}${API_ENDPOINTS.appointments.reasonTypes}`);
  }

  // Cross-controller on purpose -- same reasoning as listReasonTypes() above. The "Próximas
  // citas" panel's detail modal needs the exact start/end, assigned professional and notes for a
  // single appointment, which only AppointmentsController exposes. Dashboard owns its own
  // AppointmentDetailDto (a narrow subset of AppointmentDto's fields) instead of importing
  // appointments' TS artifacts.
  public getAppointmentById(id: string): Observable<AppointmentDetailDto> {
    return this.http.get<AppointmentDetailDto>(`${this.apiUrl}${API_ENDPOINTS.appointments.byId(id)}`);
  }

  // Cross-controller on purpose -- same reasoning as getAppointmentById() above. Cancels an
  // appointment from the dashboard's own appointment detail modal, reusing AppointmentsController's
  // endpoint instead of duplicating it under dashboard.
  public cancelAppointment(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}${API_ENDPOINTS.appointments.byId(id)}`);
  }

  // Cross-controller on purpose -- same reasoning as listReasonTypes() above. Dashboard owns its
  // own ProfessionalDto/PagedProfessionalsDto copy per the architecture rule forbidding
  // cross-feature imports between dashboard and appointments.
  public listProfessionals(): Observable<PagedProfessionalsDto> {
    return this.http.get<PagedProfessionalsDto>(`${this.apiUrl}${API_ENDPOINTS.appointments.professionals}`);
  }

  // Cross-controller on purpose -- same reasoning as listReasonTypes() above. The appointment
  // detail modal's owner/patient header needs the client's name, phone and patients, which only
  // ClientsController exposes. Dashboard owns its own ClientRefDto (a narrow subset of ClientDto's
  // fields) instead of importing clients' TS artifacts -- mirrors appointments'
  // AppointmentClientLookupRemoteDatasource.getById.
  public getClientById(id: string): Observable<ClientRefDto> {
    return this.http.get<ClientRefDto>(`${this.apiUrl}${API_ENDPOINTS.clients.byId(id)}`);
  }

  // Cross-controller on purpose -- same reasoning as getClientById() above. Search-as-you-type
  // client picker for the task drawer; mirrors AppointmentClientLookupRemoteDatasource.search's
  // exact query params (an empty term is treated as unfiltered by the backend).
  public searchClients(term: string): Observable<PagedClientRefsDto> {
    return this.http.get<PagedClientRefsDto>(`${this.apiUrl}${API_ENDPOINTS.clients.search}`, {
      params: { term, page: 1, pageSize: 20, sortBy: 'firstName', sortDir: 'asc' },
    });
  }

  public createTask(payload: Record<string, unknown>): Observable<TaskDto> {
    return this.http.post<TaskDto>(`${this.apiUrl}${API_ENDPOINTS.tasks.list}`, payload);
  }

  public updateTask(id: string, payload: Record<string, unknown>): Observable<TaskDto> {
    return this.http.patch<TaskDto>(`${this.apiUrl}${API_ENDPOINTS.tasks.byId(id)}`, payload);
  }

  public toggleTask(id: string, payload: Record<string, unknown>): Observable<TaskDto> {
    return this.http.patch<TaskDto>(`${this.apiUrl}${API_ENDPOINTS.tasks.byId(id)}`, payload);
  }

  public deleteTask(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}${API_ENDPOINTS.tasks.byId(id)}`);
  }

  // status: 1 = pending, 2 = completed -- backend TasksController.GetTasks defaults to 1 when
  // omitted, but this datasource always passes it explicitly (see IDashboardRepository.getTasks).
  // Response is PagedDto<DashboardTaskDto> (Items array), NOT a bare array -- see PagedTasksDto.
  // `page`/`pageSize` are sent only when given: omitting both keeps the backend's default of the
  // first 200 (what the "Completadas" segment's first fetch relies on).
  public getTasks(status: 1 | 2, page?: number, pageSize?: number): Observable<PagedTasksDto> {
    const params: Record<string, number> = { status };
    if (page !== undefined) params['page'] = page;
    if (pageSize !== undefined) params['pageSize'] = pageSize;
    return this.http.get<PagedTasksDto>(`${this.apiUrl}${API_ENDPOINTS.tasks.list}`, { params });
  }
}
